/**
 * Edge-function contracts + a thin typed invoke helper.
 *
 * The platform console is a pure client of the InsForge edge functions: every
 * tenant mutation and every MFA step goes through `insforge.functions.invoke`.
 * These types are the SHARED contract both ends agree on (see the milestone
 * spec); the slugs and payload shapes must not drift.
 */

import type { TenantCreateInput, Tenant } from '@obrava/shared';
import type { PlatformAuditAction } from '@obrava/shared';
import { insforge } from './insforge.js';
import type {
  MfaEnrollResponse,
  MfaVerifyResponse,
  OwnerInfo,
  OwnerResetResponse,
  OwnerCreateResponse,
} from './types.js';

/** A normalized invoke result: data present on success, a message on failure. */
export interface InvokeResult<T> {
  readonly data: T | null;
  readonly errorMessage: string | null;
  /**
   * The STABLE error code from the edge function body (`{ error: '<code>' }`),
   * surfaced by the SDK as `InsForgeError.error`. Callers map this to a precise
   * i18n message (e.g. 'tax_id_taken'); `errorMessage` is only the raw text.
   */
  readonly errorCode: string | null;
}

/**
 * Invoke an edge function and normalize the `{ data, error }` envelope. Errors
 * are never silenced: the caller always gets a code + message it can surface
 * via i18n.
 */
async function invoke<T>(
  slug: string,
  body: Record<string, unknown>,
): Promise<InvokeResult<T>> {
  const { data, error } = await insforge.functions.invoke<T>(slug, { body });
  if (error) {
    const code = (error as { error?: string }).error ?? null;
    return { data: null, errorMessage: error.message ?? null, errorCode: code };
  }
  return { data, errorMessage: null, errorCode: null };
}

// ── MFA ──────────────────────────────────────────────────────────────────────

/**
 * Start TOTP enrollment for the current platform user. Stores the secret in the
 * server-side `platform_mfa_secrets` table (never returned again afterwards).
 *
 * RE-ENROLLMENT (step-up): if the user is ALREADY enrolled, the server will not
 * overwrite the existing secret unless the caller proves possession of the
 * current authenticator by passing a valid `currentCode` (a fresh 6-digit TOTP
 * from the existing secret). Without it the function rejects with
 * `reenroll_requires_current_code`. A fresh enrollment omits `currentCode`.
 */
export function mfaEnroll(
  currentCode?: string,
): Promise<InvokeResult<MfaEnrollResponse>> {
  const body: Record<string, unknown> = currentCode ? { currentCode } : {};
  return invoke<MfaEnrollResponse>('mfa-enroll', body);
}

/** Verify a 6-digit TOTP code; first success flips mfa_enabled on. */
export function mfaVerify(code: string): Promise<InvokeResult<MfaVerifyResponse>> {
  return invoke<MfaVerifyResponse>('mfa-verify', { code });
}

// ── Tenant owner (the obrador admin) ──────────────────────────────────────────

/** Read the obrador owner identity + account status (no operational data). */
export function tenantOwnerGet(tenantId: string): Promise<InvokeResult<{ owner: OwnerInfo }>> {
  return invoke<{ owner: OwnerInfo }>('tenant-owner', { tenantId, action: 'get' });
}

/** Trigger a password-reset / invite (a 6-digit code) to the owner's email. Audited. */
export function tenantOwnerSendReset(
  tenantId: string,
): Promise<InvokeResult<OwnerResetResponse>> {
  return invoke<OwnerResetResponse>('tenant-owner', { tenantId, action: 'send_reset' });
}

/**
 * Provision the obrador owner account directly (auth user + public.users +
 * 'admin' role), then email them a code to set their own password. Audited.
 */
export function tenantOwnerCreate(
  tenantId: string,
  input: { readonly email: string; readonly fullName: string; readonly password?: string },
): Promise<InvokeResult<OwnerCreateResponse>> {
  return invoke<OwnerCreateResponse>('tenant-owner', {
    tenantId,
    action: 'create',
    email: input.email,
    fullName: input.fullName,
    // Omit when blank: the backend then emails a set-password code instead.
    ...(input.password ? { password: input.password } : {}),
  });
}

// ── Tenant (obrador) provisioning ─────────────────────────────────────────────

/** Request body for `tenant-create` (camelCase wire contract). */
export interface TenantCreateRequest {
  readonly name: string;
  readonly legalName?: string;
  readonly taxId?: string;
  readonly sector: TenantCreateInput['sector'];
  readonly plan: Tenant['plan'];
  readonly language: TenantCreateInput['language'];
  readonly adminEmail?: string;
}

/** Response of every tenant mutation function. */
export interface TenantMutationResponse {
  readonly tenant: Tenant;
}

/** The mutating actions `tenant-update` accepts. */
export type TenantUpdateAction =
  | 'update'
  | 'suspend'
  | 'reactivate'
  | 'soft_delete';

/**
 * Editable fiscal / plan fields the console may patch.
 *
 * NOTE: unlike `tenant-create` (camelCase wire contract, mapped server-side),
 * `tenant-update` applies the patch keys directly against its snake_case
 * column whitelist (`name`, `legal_name`, `tax_id`, `sector`, `plan`). So this
 * patch MUST use the snake_case column names the function whitelists.
 */
export interface TenantUpdatePatch {
  readonly name?: string;
  readonly legal_name?: string;
  readonly tax_id?: string;
  readonly sector?: Tenant['sector'];
  readonly plan?: Tenant['plan'];
}

/** Request body for `tenant-update`. `reason` is required for non-`update` actions. */
export interface TenantUpdateRequest {
  readonly tenantId: string;
  readonly action: TenantUpdateAction;
  readonly patch?: TenantUpdatePatch;
  readonly reason?: string;
}

/** Create a tenant: deploys the default structure + invites the admin server-side. */
export function tenantCreate(
  req: TenantCreateRequest,
): Promise<InvokeResult<TenantMutationResponse>> {
  return invoke<TenantMutationResponse>(
    'tenant-create',
    req as unknown as Record<string, unknown>,
  );
}

/** Update / suspend / reactivate / soft-delete a tenant (audited server-side). */
export function tenantUpdate(
  req: TenantUpdateRequest,
): Promise<InvokeResult<TenantMutationResponse>> {
  return invoke<TenantMutationResponse>(
    'tenant-update',
    req as unknown as Record<string, unknown>,
  );
}

/** Map a tenant-update action to the audit action it records (documentation aid). */
export const AUDIT_ACTION_BY_UPDATE: Readonly<
  Record<TenantUpdateAction, PlatformAuditAction>
> = {
  update: 'tenant_update',
  suspend: 'tenant_suspend',
  reactivate: 'tenant_reactivate',
  soft_delete: 'tenant_soft_delete',
};

// ── Tenant activity log (the obrador's OWN audit_log) ─────────────────────────

/** One audit entry, actor already resolved to a display name (no raw field values). */
export interface AuditEntry {
  readonly id: string;
  readonly created_at: string;
  readonly action: string;
  readonly entity_type: string;
  readonly entity_id: string | null;
  readonly actor_kind: 'user' | 'support' | 'system';
  readonly actor_name: string | null;
  readonly is_support_access: boolean;
  readonly reason: string | null;
  readonly entity_name: string | null;
}

/** A page of the activity log. */
export interface AuditPage {
  readonly entries: readonly AuditEntry[];
  readonly hasMore: boolean;
}

/** Filters / pagination for a tenant-audit-log read. */
export interface TenantAuditListOptions {
  readonly limit?: number;
  readonly offset?: number;
  readonly action?: string;
  readonly entityType?: string;
}

/**
 * Read a page of a tenant's own activity log. Each FIRST-page view (offset 0) is
 * recorded — visibly to the client — in the tenant's audit_log by the server
 * (the "support access logged and visible to the client" invariant).
 */
export function tenantAuditList(
  tenantId: string,
  opts: TenantAuditListOptions = {},
): Promise<InvokeResult<AuditPage>> {
  return invoke<AuditPage>('tenant-audit-log', {
    tenantId,
    ...(opts.limit != null ? { limit: opts.limit } : {}),
    ...(opts.offset != null ? { offset: opts.offset } : {}),
    ...(opts.action ? { action: opts.action } : {}),
    ...(opts.entityType ? { entityType: opts.entityType } : {}),
  });
}
